import { useState } from 'react';
import { 
  ChatBubbleLeftRightIcon, 
  LightBulbIcon, 
  UserGroupIcon
} from '@heroicons/react/24/outline';

// Import from the analytics feature module
import { 
  SuggestionsList, 
  StudentClustering, 
  ChatInterface, 
  useAnalytics 
} from '../features/analytics';
import { Button, LoadingSpinner } from './ui';
import { ActionableSuggestionsProps } from '../types';

const ActionableSuggestions: React.FC<ActionableSuggestionsProps> = ({ classId }) => {
  const [activeTab, setActiveTab] = useState<'suggestions' | 'clustering' | 'chat'>('suggestions');

  // Use the custom hook for analytics data
  const { studentClusters, suggestions, loading, error } = useAnalytics(classId);

  if (loading) {
    return (
      <div className="mt-12">
        <div className="bg-gradient-to-r from-emerald-50 to-lime-50 rounded-2xl p-8">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-12">
        <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-8">
          <div className="text-center">
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-12">
      <div className="bg-gradient-to-r from-emerald-50 to-lime-50 rounded-2xl p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Actionable Suggestions</h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto">
            Evidence-based pedagogical recommendations tailored to your classroom insights and student feedback. 
            These suggestions are backed by educational research and customized for your specific class composition.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg p-1 shadow-md flex">
            <Button
              variant={activeTab === 'suggestions' ? 'primary' : 'ghost'}
              onClick={() => setActiveTab('suggestions')}
              leftIcon={<LightBulbIcon className="h-5 w-5" />}
              className="rounded-r-none"
            >
              Pedagogical Suggestions
            </Button>
            <Button
              variant={activeTab === 'clustering' ? 'primary' : 'ghost'}
              onClick={() => setActiveTab('clustering')}
              leftIcon={<UserGroupIcon className="h-5 w-5" />}
              className="rounded-none"
            >
              Student Groups
            </Button>
            <Button
              variant={activeTab === 'chat' ? 'primary' : 'ghost'}
              onClick={() => setActiveTab('chat')}
              leftIcon={<ChatBubbleLeftRightIcon className="h-5 w-5" />}
              className="rounded-l-none"
            >
              Ask Questions
            </Button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-96">
          {activeTab === 'suggestions' && <SuggestionsList suggestions={suggestions} />}
          {activeTab === 'clustering' && <StudentClustering clusters={studentClusters} />}
          {activeTab === 'chat' && <ChatInterface />}
        </div>
      </div>
    </div>
  );
};

export default ActionableSuggestions; 